// The browser half of the CLI device authorization flow (`SC-F217`):
// `POST /v1/device/lookup` and `POST /v1/device/approve`.
//
// These are the only two calls a confirmation screen makes, and the split between
// them is deliberate: the lookup is a READ that decides nothing, so a human can
// learn what they are being asked to approve BEFORE approving it. A screen that
// could only find out by saying yes would be a screen that cannot ask.
//
// **Both routes demand a signed-in session and refuse an API key of either kind.**
// `internal/pat`'s `requireSessionCredential` is the enforcement, and the rule it
// keeps is that a credential must never be able to mint another credential: a
// leaked general key could otherwise start a flow and approve it. Neither route
// appears in the scoped-token route map at all, so a `sluice_ft_` token fails
// closed with `403 insufficient_scope` (`SC-F207`).
//
// **Approving is step-up gated; denying is not.** The asymmetry is the point.
// Approval causes a long-lived general API key to exist, which is exactly what
// `SC-F211` gates, so it needs the same fresh identity proof. Denial creates
// nothing, so requiring a re-verification to say "no" would push a user who does
// not recognize a request into closing the tab instead — leaving the flow pending
// for whoever holds the device code. The safe answer has to be the cheap one.

import { sendJSON } from "./http.js";

/**
 * DeviceLookup is what the confirmation screen renders. Nothing in it is
 * authority: no device code, no account, no credential. A caller who guesses a
 * user code learns only that some CLI somewhere is waiting.
 */
export interface DeviceLookup {
  /** The user code echoed back in grouped form, so the screen shows the human the
   * same string they are reading off the other machine. */
  user_code: string;
  /** The CLI's own sanitized self-description. Untrusted input, rendered as
   * text. */
  client: string;
  /** The address the flow was started from. This is the strongest signal a human
   * has: an unexpected address is the tell that somebody else's CLI is asking. */
  ip: string;
  /** The lifetime the credential will have if approved, so the human agrees to a
   * stated duration rather than to "a credential". */
  lifetime_seconds: number;
  /** Seconds left to decide. */
  expires_in: number;
  /** `pending` | `approved` | `denied` | `expired`. A screen renders "already
   * decided" rather than offering a button that will answer `409`. */
  status: string;
  /** Always true today: approving needs a fresh step-up grant. Read it rather
   * than assuming, so a client learns the requirement before submitting instead
   * of discovering it through a `403`. */
  verification_required: boolean;
}

/** DeviceDecision confirms what was recorded. */
export interface DeviceDecision {
  status: string;
  user_code: string;
}

/**
 * lookupDevice resolves a user code to the facts a human needs.
 *
 * Every failure — unknown code, mistyped code, expired flow — answers the same
 * opaque `401 invalid_token`, so this has exactly one failure path and cannot be
 * used to learn which codes are live.
 *
 * The code may be typed with or without the grouping dash; the server
 * canonicalizes it (`pat.CanonicalUserCode`), so the client sends what the human
 * typed rather than guessing at the format.
 */
export function lookupDevice(userCode: string): Promise<DeviceLookup> {
  return sendJSON<DeviceLookup>("/v1/device/lookup", "POST", { user_code: userCode });
}

/**
 * decideDevice records the human's decision.
 *
 *   // deny: no grant needed
 *   await decideDevice(code, false);
 *   // approve: a fresh step-up grant is required
 *   const grant = await verifyWithPasskey({ action: "create_api_key" });
 *   await decideDevice(code, true, grant);
 *
 * `approve` is sent explicitly and is never defaulted, because the two values are
 * not equally safe: a body that failed to carry the field must not read as
 * "approve". The server refuses a missing field with `validation_failed` for the
 * same reason.
 *
 * On approval without a usable grant the server answers `verification_required`
 * or `verification_invalid` — 403, not 401, because the caller IS authenticated
 * and merely lacks a fresh proof. The caller re-opens the verify dialog and
 * retries the same decision; the user code and the typed state are untouched
 * (`01` §3.2).
 */
export function decideDevice(
  userCode: string,
  approve: boolean,
  verification?: string,
): Promise<DeviceDecision> {
  return sendJSON<DeviceDecision>(
    "/v1/device/approve",
    "POST",
    { user_code: userCode, approve },
    approve && verification ? { verification } : {},
  );
}
